<template>
  <div class="d-flex justify-center filter-hover" data-testid="my-reporting-units-filter">
    <div class="gemini-loader-container" :class="{ 'loading': loading }">
      <div class="border-on d-flex shadow-on radius-small cycle-filter-bg d-flex align-center gap-2">
        <div class="d-flex flex-wrap align-center gap-2 filter-container" data-testid="my-reporting-units-filter__container">
          <div
            v-if="periodFilterConfig || loading"
            class="filter-period d-flex flex-column gap-2 px-3 py-2 hoverable periodfilter-label"
            data-testid="my-reporting-units-filter__period"
          >
            <div class="grey--text text--darken-1 line-1 noTransform periodfilte">
              <span v-if="periodFilterConfig">{{ periodFilterConfig.name }}</span>
              <v-skeleton-loader v-else-if="loading" type="text" width="40px" />
            </div>
            <div class="font-weight-bold">
              <v-skeleton-loader v-if="loading" type="text" width="60px" />
              <v-menu v-else offset-y open-on-hover :close-delay="200" transition="slide-y-transition">
                <template #activator="{ on, attrs }">
                  <div class="filter-hide cursor-pointer noTransform" v-bind="attrs" v-on="on">
                    <span class="filter-text">{{ selectedPeriodDisplay }}</span>
                    <v-icon class="ml-1">mdi-chevron-down</v-icon>
                  </div>
                </template>
                <perfect-scrollbar style="max-height: 300px; min-width: 120px;">
                  <v-list dense class="pa-0 border-left-default">
                    <v-list-item
                      v-for="item in periodFilterConfig.data"
                      :key="item._id"
                      @click="changePeriod(item)"
                      data-testid="my-reporting-units-filter__period-item"
                    >
                      <v-list-item-title class="v-list-item-title">{{ item.name }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </perfect-scrollbar>
              </v-menu>
            </div>
          </div>

          <div class="filter-divider" v-if="(periodFilterConfig || loading) && (networkFilterConfig || loading)"></div>

          <!-- Network Filter -->
          <div
            v-if="networkFilterConfig || loading"
            class="filter-network d-flex flex-column gap-2 px-3 py-2 hoverable networkfilter-label"
            data-testid="my-reporting-units-filter__network"
          >
            <div class="grey--text text--darken-1 line-1 noTransform">
              <span v-if="networkFilterConfig">{{ networkFilterConfig.name }}</span>
              <v-skeleton-loader v-else-if="loading" type="text" width="50px" />
            </div>
            <div class="font-weight-bold">
              <v-skeleton-loader v-if="loading" type="text" width="80px" />
              <v-menu v-else offset-y open-on-hover :close-delay="200" transition="slide-y-transition">
                <template #activator="{ on, attrs }">
                  <div class="filter-hide cursor-pointer noTransform" v-bind="attrs" v-on="on">
                    <span class="filter-text" :title="selectedNetworkDisplay">{{ selectedNetworkDisplay }}</span>
                    <v-icon class="ml-1">mdi-chevron-down</v-icon>
                  </div>
                </template>
                <div class="white pa-2" @click.stop>
                  <lb-string v-model="networkSearch" hidedetails label="Search" />
                </div>
                <perfect-scrollbar style="max-height: 300px; min-width: 150px;">
                  <v-list dense class="pa-0 border-left-default">
                    <v-list-item @click="changeNetwork(null)" data-testid="my-reporting-units-filter__network-all">
                      <v-list-item-title class="v-list-item-title">All</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-for="item in filteredNetworkList"
                      :key="item._id"
                      @click="changeNetwork(item)"
                      data-testid="my-reporting-units-filter__network-item"
                    >
                      <v-list-item-title class="v-list-item-title">{{ item.name }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </perfect-scrollbar>
              </v-menu>
            </div>
          </div>

          <div class="filter-divider" v-if="(networkFilterConfig || loading) && (regionFilterConfig || loading)"></div>

          <!-- Region Filter -->
          <div
            v-if="regionFilterConfig || loading"
            class="filter-region d-flex flex-column gap-2 px-3 py-2 hoverable regionfilter-label"
            data-testid="my-reporting-units-filter__region"
          >
            <div class="grey--text text--darken-1 line-1 noTransform">
              <span v-if="regionFilterConfig">{{ regionFilterConfig.name }}</span>
              <v-skeleton-loader v-else-if="loading" type="text" width="50px" />
            </div>
            <div class="font-weight-bold">
              <v-skeleton-loader v-if="loading" type="text" width="70px" />
              <v-menu v-else offset-y open-on-hover :close-delay="200" transition="slide-y-transition">
                <template #activator="{ on, attrs }">
                  <div class="filter-hide cursor-pointer noTransform" v-bind="attrs" v-on="on">
                    <span class="filter-text" :title="selectedRegionDisplay">{{ selectedRegionDisplay }}</span>
                    <v-icon class="ml-1">mdi-chevron-down</v-icon>
                  </div>
                </template>
                <div class="white pa-2" @click.stop>
                  <lb-string v-model="regionSearch" hidedetails label="Search" />
                </div>
                <perfect-scrollbar style="max-height: 300px; min-width: 150px;">
                  <v-list dense class="pa-0 border-left-default">
                    <v-list-item @click="changeRegion(null)" data-testid="my-reporting-units-filter__region-all">
                      <v-list-item-title class="v-list-item-title">All</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-for="item in filteredRegionList"
                      :key="item._id"
                      @click="changeRegion(item)"
                      data-testid="my-reporting-units-filter__region-item"
                    >
                      <v-list-item-title class="v-list-item-title">{{ item.name }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </perfect-scrollbar>
              </v-menu>
            </div>
          </div>

          <div class="filter-divider" v-if="(regionFilterConfig || loading) && (countryFilterConfig || loading)"></div>

          <!-- Country Filter -->
          <div
            v-if="countryFilterConfig || loading"
            class="filter-country d-flex flex-column gap-2 px-3 py-2 hoverable countryfilter-label"
            data-testid="my-reporting-units-filter__country"
          >
            <div class="grey--text text--darken-1 line-1 noTransform">
              <span v-if="countryFilterConfig">{{ countryFilterConfig.name }}</span>
              <v-skeleton-loader v-else-if="loading" type="text" width="50px" />
            </div>
            <div class="font-weight-bold">
              <v-skeleton-loader v-if="loading" type="text" width="70px" />
              <v-menu v-else offset-y open-on-hover :close-delay="200" transition="slide-y-transition">
                <template #activator="{ on, attrs }">
                  <div class="filter-hide cursor-pointer noTransform" v-bind="attrs" v-on="on">
                    <span class="filter-text" :title="selectedCountryDisplay">{{ selectedCountryDisplay }}</span>
                    <v-icon class="ml-1">mdi-chevron-down</v-icon>
                  </div>
                </template>
                <div class="white pa-2" @click.stop>
                  <lb-string v-model="countrySearch" hidedetails label="Search" />
                </div>
                <perfect-scrollbar style="max-height: 300px; min-width: 150px;">
                  <v-list dense class="pa-0 border-left-default">
                    <v-list-item @click="changeCountry(null)" data-testid="my-reporting-units-filter__country-all">
                      <v-list-item-title class="v-list-item-title">All</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-for="item in filteredCountryList"
                      :key="item._id"
                      @click="changeCountry(item)"
                      data-testid="my-reporting-units-filter__country-item"
                    >
                      <v-list-item-title class="v-list-item-title">{{ item.name }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </perfect-scrollbar>
              </v-menu>
            </div>
          </div>

          <div class="filter-divider" v-if="(countryFilterConfig || loading) && (allRutypeConfig || loading)"></div>

          <!-- All Users Set Filter -->
          <div
            v-if="allRutypeConfig || loading"
            class="filter-allusersset d-flex flex-column gap-2 px-3 py-2 hoverable"
            data-testid="my-reporting-units-filter__allusersset"
          >
            <div class="grey--text text--darken-1 line-1 noTransform">
              <span v-if="allRutypeConfig">{{ allRutypeConfig.name }}</span>
              <v-skeleton-loader v-else-if="loading" type="text" width="60px" />
            </div>
            <div class="font-weight-bold">
              <v-skeleton-loader v-if="loading" type="text" width="50px" />
              <v-menu v-else offset-y open-on-hover :close-delay="200" transition="slide-y-transition">
                <template #activator="{ on, attrs }">
                  <div class="filter-hide cursor-pointer noTransform" v-bind="attrs" v-on="on">
                    <span class="filter-text" :class="{ 'text-uppercase': selectedRUTypeSet }">{{ selectedRuTypeSetDisplay || 'All' }}</span>
                    <v-icon class="ml-1">mdi-chevron-down</v-icon>
                  </div>
                </template>
                <perfect-scrollbar style="max-height: 200px; min-width: 100px;">
                  <v-list dense class="pa-0 border-left-default">
                    <v-list-item @click="changeRutype(null)" data-testid="my-reporting-units-filter__allusersset-all">
                      <v-list-item-title class="v-list-item-title">All</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-for="item in allRutypeConfig.data"
                      :key="item._id"
                      @click="changeRutype(item)"
                      data-testid="my-reporting-units-filter__allusersset-item"
                    >
                      <v-list-item-title class="v-list-item-title text-uppercase">{{ item.name }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </perfect-scrollbar>
              </v-menu>
            </div>
          </div>

          <div class="filter-divider" v-if="(allRutypeConfig || loading) && (codeFilterConfig || loading)"></div>

          <!-- RU Code Filter -->
          <div
            v-if="codeFilterConfig || loading"
            class="filter-code d-flex flex-column gap-2 px-3 py-2 hoverable codefilter-label"
            data-testid="my-reporting-units-filter__code"
          >
            <div class="grey--text text--darken-1 line-1 noTransform">
              <span v-if="codeFilterConfig">{{ codeFilterConfig.name }}</span>
              <v-skeleton-loader v-else-if="loading" type="text" width="50px" />
            </div>
            <div class="font-weight-bold">
              <v-skeleton-loader v-if="loading" type="text" width="100px" />
              <v-menu v-else offset-y open-on-hover :close-delay="200" transition="slide-y-transition">
                <template #activator="{ on, attrs }">
                  <div class="filter-hide cursor-pointer noTransform" v-bind="attrs" v-on="on">
                    <span class="filter-text" :title="selectedCodeDisplay">{{ truncateText(selectedCodeDisplay, 20) }}</span>
                    <v-icon class="ml-1">mdi-chevron-down</v-icon>
                  </div>
                </template>
                <div class="white pa-2" @click.stop>
                  <lb-string v-model="codeSearch" hidedetails label="Search" />
                </div>
                <perfect-scrollbar style="max-height: 300px; min-width: 200px;">
                  <v-list dense class="pa-0 border-left-default">
                    <v-list-item @click="changeCode(null)" data-testid="my-reporting-units-filter__code-all">
                      <v-list-item-title class="v-list-item-title">All</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-for="item in filteredCodeList"
                      :key="item._id"
                      @click="changeCode(item)"
                      data-testid="my-reporting-units-filter__code-item"
                    >
                      <v-list-item-title class="v-list-item-title">{{ item.code }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </perfect-scrollbar>
              </v-menu>
            </div>
          </div>

        </div>

        <div class="filter-divider"></div>

        <!-- Apply Button -->
        <div class="d-flex align-center gap-2 px-2">
          <v-btn
            color="primary"
            class="apply-btn"
            :disabled="!hasPendingChanges"
            @click="applyFilters"
            data-testid="my-reporting-units-filter__apply-btn"
          >
            <span class="text-capitalize">Apply</span>
          </v-btn>
        </div>

        <!-- <div class="filter-divider"></div> -->

        <!-- Reset Button -->
        <!-- <div class="text-center px-2 py-2">
          <v-tooltip bottom content-class="tooltip-bottom">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click="resetFilters"
                data-testid="my-reporting-units-filter__reset-btn"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Reset filters</span>
          </v-tooltip>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script>
/**
 * MyReportingUnitsFilter - Custom filter component for My Reporting Units page
 * Handles Year, Period, Network, Region, Country, All Users Set, and RU Code filters
 */
export default {
  name: 'MyReportingUnitsFilter',

  props: {
    /**
     * Filter configuration array with structure:
     * [{ key: 'year', name: 'Year', data: [...], value: 'name', type: 'singleselect', defaultValue: '...' }]
     */
    filterOptions: {
      type: Array,
      default: () => [],
    },
    /**
     * Loading state for the filter component
     */
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Selected filter values
      selectedYear: null,
      selectedPeriod: null,
      selectedNetwork: null,
      selectedRegion: null,
      selectedCountry: null,
      selectedRUTypeSet: null,
      selectedCode: null,

      // Search strings for searchable filters
      networkSearch: '',
      regionSearch: '',
      countrySearch: '',
      codeSearch: '',

      // Track if filters have pending changes
      hasPendingChanges: false,

      // Store last applied filters for comparison
      lastAppliedFilters: null,
    };
  },

  computed: {
    /**
     * Get Year filter configuration from filterOptions
     */
    // yearFilterConfig() {
    //   return this.filterOptions.find((f) => f.key === 'year') || null;
    // },

    /**
     * Get Period filter configuration from filterOptions
     */
    periodFilterConfig() {
      return this.filterOptions.find((f) => f.key === 'period') || null;
    },

    /**
     * Get Network filter configuration from filterOptions
     */
    networkFilterConfig() {
      return this.filterOptions.find((f) => f.key === 'network') || null;
    },

    /**
     * Get Region filter configuration from filterOptions
     */
    regionFilterConfig() {
      return this.filterOptions.find((f) => f.key === 'region') || null;
    },

    /**
     * Get Country filter configuration from filterOptions
     */
    countryFilterConfig() {
      return this.filterOptions.find((f) => f.key === 'country') || null;
    },

    /**
     * Get All Users Set filter configuration from filterOptions
     */
    allRutypeConfig() {
      return this.filterOptions.find((f) => f.key === 'reportingunitTypes') || null;
    },

    /**
     * Get RU Code filter configuration from filterOptions
     */
    codeFilterConfig() {
      return this.filterOptions.find((f) => f.key === 'code') || null;
    },

    /**
     * Display text for selected year
     */
    // selectedYearDisplay() {
    //   if (!this.selectedYear) {
    //     return this.yearFilterConfig?.defaultValue || 'All';
    //   }
    //   const item = this.yearFilterConfig?.data?.find((d) => d._id === this.selectedYear);
    //   return item?.name || this.selectedYear;
    // },

    /**
     * Display text for selected period
     */
    selectedPeriodDisplay() {
      if (!this.selectedPeriod) {
        return this.periodFilterConfig?.defaultValue || 'All';
      }
      const item = this.periodFilterConfig?.data?.find((d) => d._id === this.selectedPeriod._id);
      return item?.name || this.selectedPeriod.name || 'All';
    },

    /**
     * Display text for selected network
     */
    selectedNetworkDisplay() {
      if (!this.selectedNetwork) return 'All';
      return this.selectedNetwork.name || 'All';
    },

    /**
     * Display text for selected region
     */
    selectedRegionDisplay() {
      if (!this.selectedRegion) return 'All';
      return this.selectedRegion.name || 'All';
    },

    /**
     * Display text for selected country
     */
    selectedCountryDisplay() {
      if (!this.selectedCountry) return 'All';
      return this.selectedCountry.name || 'All';
    },

    /**
     * Display text for selected all users set
     */
    selectedRuTypeSetDisplay() {
      if (!this.selectedRUTypeSet) return 'All';
      return this.selectedRUTypeSet.name || 'All';
    },

    /**
     * Display text for selected RU code
     */
    selectedCodeDisplay() {
      if (!this.selectedCode) return 'All';
      return this.selectedCode.code || 'All';
    },

    /**
     * Filtered network list based on search
     */
    filteredNetworkList() {
      const data = this.networkFilterConfig?.data || [];
      if (!this.networkSearch) return data.slice(0, 25);
      const search = this.networkSearch.toLowerCase();
      return data.filter((item) => item.name?.toLowerCase().includes(search)).slice(0, 25);
    },

    /**
     * Filtered region list based on search
     */
    filteredRegionList() {
      const data = this.regionFilterConfig?.data || [];
      if (!this.regionSearch) return data.slice(0, 25);
      const search = this.regionSearch.toLowerCase();
      return data.filter((item) => item.name?.toLowerCase().includes(search)).slice(0, 25);
    },

    /**
     * Filtered country list based on search
     */
    filteredCountryList() {
      const data = this.countryFilterConfig?.data || [];
      if (!this.countrySearch) return data.slice(0, 25);
      const search = this.countrySearch.toLowerCase();
      return data.filter((item) => item.name?.toLowerCase().includes(search)).slice(0, 25);
    },

    /**
     * Filtered code list based on search
     */
    filteredCodeList() {
      const data = this.codeFilterConfig?.data || [];
      if (!this.codeSearch) return data.slice(0, 25);
      const search = this.codeSearch.toLowerCase();
      return data.filter((item) => item.code?.toLowerCase().includes(search)).slice(0, 25);
    },
  },

  watch: {
    /**
     * Watch filterOptions to set default values when data loads
     */
    filterOptions: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.initializeDefaults();
        }
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    /**
     * Initialize default filter values from filterOptions
     */
    initializeDefaults() {
      // Set year default
      if (this.yearFilterConfig?.defaultValue && !this.selectedYear) {
        this.selectedYear = this.yearFilterConfig.defaultValue;
      }
      // Set period default
      if (this.periodFilterConfig?.defaultValue && !this.selectedPeriod) {
        const periodItem = this.periodFilterConfig.data?.find(
          (p) => p.name === this.periodFilterConfig.defaultValue
        );
        if (periodItem) {
          this.selectedPeriod = periodItem;
        }
      }
      // Store initial state as last applied
      this.lastAppliedFilters = this.collectCurrentFilters();
    },

    /**
     * Collect current filter values into an object
     */
    collectCurrentFilters() {
      return {
        // year: this.selectedYear,
        period: this.selectedPeriod?._id || null,
        network: this.selectedNetwork?._id || null,
        region: this.selectedRegion?._id || null,
        country: this.selectedCountry?._id || null,
        ruTypes: this.selectedRUTypeSet?._id || null,
        code: this.selectedCode?._id || null,
      };
    },

    /**
     * Mark filters as having pending changes
     */
    markPending() {
      const current = this.collectCurrentFilters();
      const last = this.lastAppliedFilters || {};
      this.hasPendingChanges = JSON.stringify(current) !== JSON.stringify(last);
    },

    /**
     * Change year filter value
     * @param {string} yearId - Year ID to select
     */
    changeYear(yearId) {
      this.selectedYear = yearId;
      this.markPending();
    },

    /**
     * Change period filter value
     * @param {Object} periodItem - Period item to select
     */
    changePeriod(periodItem) {
      this.selectedPeriod = periodItem;
      this.markPending();
    },

    /**
     * Change network filter value
     * @param {Object|null} networkItem - Network item to select, null for All
     */
    changeNetwork(networkItem) {
      this.selectedNetwork = networkItem;
      this.networkSearch = '';
      this.markPending();
    },

    /**
     * Change region filter value
     * @param {Object|null} regionItem - Region item to select, null for All
     */
    changeRegion(regionItem) {
      this.selectedRegion = regionItem;
      this.regionSearch = '';
      this.markPending();
    },

    /**
     * Change country filter value
     * @param {Object|null} countryItem - Country item to select, null for All
     */
    changeCountry(countryItem) {
      this.selectedCountry = countryItem;
      this.countrySearch = '';
      this.markPending();
    },

    /**
     * Change all users set filter value
     * @param {Object|null} item - All users set item to select, null for All
     */
    changeRutype(item) {
      this.selectedRUTypeSet = item;
      this.markPending();
    },

    /**
     * Change RU code filter value
     * @param {Object|null} codeItem - Code item to select, null for All
     */
    changeCode(codeItem) {
      this.selectedCode = codeItem;
      this.codeSearch = '';
      this.markPending();
    },

    /**
     * Apply filters and emit to parent
     */
    applyFilters() {
      const filterPayload = {
        filter: {
          // year: this.selectedYear ? [{ _id: this.selectedYear, name: this.selectedYear }] : [],
          period: this.selectedPeriod ? [this.selectedPeriod] : [],
          network: this.selectedNetwork ? [this.selectedNetwork] : [],
          region: this.selectedRegion ? [this.selectedRegion] : [],
          country: this.selectedCountry ? [this.selectedCountry] : [],
          ruTypes: this.selectedRUTypeSet ? [this.selectedRUTypeSet] : [],
          code: this.selectedCode ? [this.selectedCode] : [],
        },
      }; 
      this.lastAppliedFilters = this.collectCurrentFilters();
      this.hasPendingChanges = false;
      this.$emit('loadFilterData', filterPayload);
    },

    /**
     * Reset all filters to default values
     */
    resetFilters() {
      this.selectedYear = this.yearFilterConfig?.defaultValue || null;
      const periodDefault = this.periodFilterConfig?.defaultValue;
      if (periodDefault) {
        this.selectedPeriod = this.periodFilterConfig.data?.find((p) => p.name === periodDefault) || null;
      } else {
        this.selectedPeriod = null;
      }
      this.selectedNetwork = null;
      this.selectedRegion = null;
      this.selectedCountry = null;
      this.selectedRUTypeSet = null;
      this.selectedCode = null;

      // Clear search strings
      this.networkSearch = '';
      this.regionSearch = '';
      this.countrySearch = '';
      this.codeSearch = '';

      this.hasPendingChanges = false;
      this.lastAppliedFilters = this.collectCurrentFilters();
      this.$emit('resetFilters', {});
    },

    /**
     * Truncate text to specified length
     * @param {string} text - Text to truncate
     * @param {number} maxLength - Maximum length
     * @returns {string} Truncated text
     */
    truncateText(text, maxLength = 20) {
      if (!text || text.length <= maxLength) return text;
      return `${text.substring(0, maxLength)}...`;
    },
  },

  beforeDestroy() {
    // Reset component state to prevent stale data on re-entry
    this.selectedYear = null;
    this.selectedPeriod = null;
    this.selectedNetwork = null;
    this.selectedRegion = null;
    this.selectedCountry = null;
    this.selectedRUTypeSet = null;
    this.selectedCode = null;
    this.networkSearch = '';
    this.regionSearch = '';
    this.countrySearch = '';
    this.codeSearch = '';
    this.hasPendingChanges = false;
    this.lastAppliedFilters = null;
  },
};
</script>

<style scoped>
  .periodfilter-label{
    width: 170px;
  }
  .networkfilter-label{
    width: 120px;
  }
  .regionfilter-label{
    width: 120px;
  }
  .countryfilter-label{
    width: 120px;
  }
  .codefilter-label{
    width: 150px;
  }
/* Cycle filter background */
.cycle-filter-bg {
  background-color: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  padding: var(--space-5);
  box-shadow: 0px 4px 4px 0px #00000014 !important;
}

/* Filter divider */
.filter-divider {
  display: block;
  height: 36px;
  width: 1px;
  background-color: #cbcbcc;
}

/* Hoverable filter sections */
.hoverable:hover {
  background-color: var(--cycle-filter-section-bg) !important;
  border-radius: var(--radius-4);
}

/* Filter label text */
.hoverable .grey--text {
  color: #64748b !important;
  font-weight: 500;
  text-transform: none !important;
  letter-spacing: normal !important;
  font-size: var(--font-size-xxs);
}

/* Filter value text */
.hoverable .font-weight-bold {
  color: #0f172a !important;
  font-weight: 600 !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

/* Filter hide container */
.filter-hide {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #0f172a !important;
  padding: 0;
  border-radius: 0;
  min-height: 20px;
  max-width: 100%;
  overflow: hidden;
}

.filter-hide:hover {
  background-color: transparent !important;
}

.filter-hide .v-icon {
  color: #94a3b8 !important;
  font-size: 16px;
  flex-shrink: 0;
}

/* Filter text truncation */
.filter-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

/* No text transform */
.noTransform {
  text-transform: none !important;
}

/* Apply button */
.apply-btn {
  border-radius: 6px;
  font-weight: 500;
  letter-spacing: normal;
  text-transform: none !important;
}

/* Menu content styling */
.v-menu__content {
  border-radius: 12px !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  border: 1px solid #e2e8f0;
  margin-top: 8px;
}

.v-list {
  padding: 4px;
}

.v-list-item {
  margin-bottom: 2px;
}

.v-list-item:hover {
  background-color: var(--cycle-filter-section-bg) !important;
}

/* List item title styling */
.v-list-item-title {
  font-family: 'Roboto', sans-serif !important;
  font-size: var(--font-size-xs) !important;
  font-weight: 500 !important;
  color: #0f172a !important;
}

/* Gemini loader animation */
.gemini-loader-container {
  position: relative;
  border-radius: 10px;
  padding: 2px;
  background: transparent;
  transition: box-shadow 0.3s ease;
}

.gemini-loader-container.loading {
  animation: gemini-shadow-pulse 2s ease-in-out infinite;
}

.gemini-loader-container.loading::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 10px;
  padding: 2px;
  background: linear-gradient(90deg, 
    var(--logo-color1),
    var(--logo-color2),
    var(--logo-color3));
  background-size: 400% 100%;
  -webkit-mask: 
    linear-gradient(#fff 0 0) content-box, 
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  animation: gemini-border-flow 3s linear infinite;
  pointer-events: none;
}

@keyframes gemini-border-flow {
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
}

@keyframes gemini-shadow-pulse {
  0%, 100% { box-shadow: 0 0 10px rgba(66, 133, 244, 0.3); }
  50% { box-shadow: 0 0 20px rgba(66, 133, 244, 0.5); }
}
</style>
